import { useEffect, useState } from "react";
import api, { formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, User as UserIcon, KeyRound, ShieldCheck, ShieldOff, Lock } from "lucide-react";

const emptyForm = { name: "", email: "", password: "", role: "user" };

export default function UsersPage() {
    const { user: current } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const [resetTarget, setResetTarget] = useState(null);
    const [resetPassword, setResetPassword] = useState("");
    const [resetSaving, setResetSaving] = useState(false);

    const isAdmin = current?.role === "admin";

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch (err) {
            if (err.response?.status !== 403) toast.error("加载失败");
        }
        setLoading(false);
    };

    useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) return toast.error("密码至少 6 位");
        setSaving(true);
        try {
            await api.post("/users", form);
            toast.success(`账户已创建 (${form.role === "admin" ? "管理员" : "员工"})`);
            setForm(emptyForm);
            setDialogOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "创建失败");
        } finally {
            setSaving(false);
        }
    };

    const del = async (u) => {
        if (!window.confirm(`确定删除账户 ${u.email}？`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("账户已删除");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "删除失败");
        }
    };

    const toggleRole = async (u) => {
        const nextRole = u.role === "admin" ? "user" : "admin";
        const label = nextRole === "admin" ? "提升为管理员" : "降级为员工";
        if (!window.confirm(`确定将 ${u.email} ${label}？`)) return;
        try {
            await api.put(`/users/${u.id}/role`, { role: nextRole });
            toast.success(`${u.email} 已${label}`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "修改失败");
        }
    };

    const openReset = (u) => { setResetTarget(u); setResetPassword(""); };

    const submitReset = async (e) => {
        e.preventDefault();
        if (resetPassword.length < 6) return toast.error("密码至少 6 位");
        setResetSaving(true);
        try {
            await api.put(`/users/${resetTarget.id}/password`, { new_password: resetPassword });
            toast.success(`已重置 ${resetTarget.email} 的密码`);
            setResetTarget(null);
            setResetPassword("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "重置失败");
        } finally {
            setResetSaving(false);
        }
    };

    if (!isAdmin) {
        return (
            <div className="space-y-6" data-testid="users-page">
                <Card className="p-12 border-slate-200 shadow-sm text-center max-w-lg mx-auto">
                    <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                        <Lock className="h-6 w-6 text-slate-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900">仅管理员可访问</h2>
                    <p className="text-sm text-gold-500 mt-2">用户管理功能需要管理员权限。请联系管理员为你分配权限。</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="users-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">团队</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">用户管理</h1>
                    <p className="text-sm text-gold-500 mt-1">添加员工或管理员账户，共享同一份库存与销售数据</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-user-btn" className="bg-gold-600 hover:bg-gold-700 text-white gap-2">
                            <Plus className="h-4 w-4" /> 添加账户
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>添加登录账户</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4 pt-2">
                            <div className="space-y-1.5">
                                <Label>姓名 *</Label>
                                <Input data-testid="new-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                            </div>
                            <div className="space-y-1.5">
                                <Label>邮箱 *</Label>
                                <Input data-testid="new-user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                            </div>
                            <div className="space-y-1.5">
                                <Label>初始密码 * (至少 6 位)</Label>
                                <Input data-testid="new-user-password-input" type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                            </div>
                            <div className="space-y-1.5">
                                <Label>角色 *</Label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger data-testid="new-user-role-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="user">员工 · 只能开销售单、查看产品与销售</SelectItem>
                                        <SelectItem value="admin">管理员 · 拥有全部权限</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <DialogFooter className="pt-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>取消</Button>
                                <Button type="submit" data-testid="save-user-btn" disabled={saving} className="bg-gold-600 hover:bg-gold-700 text-white">
                                    {saving ? "创建中..." : "创建账户"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="border-slate-200 shadow-sm">
                {loading ? (
                    <div className="p-12 text-center text-gold-500">加载中...</div>
                ) : users.length === 0 ? (
                    <div className="p-12 text-center">
                        <UserIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                        <div className="text-sm text-gold-500">暂无账户</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-200 bg-gold-50/60">
                                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">姓名</th>
                                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">邮箱</th>
                                <th className="text-center py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">角色</th>
                                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">最后登入</th>
                                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">创建时间</th>
                                <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wider text-gold-500">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => {
                                const isSelf = u.id === current?.id;
                                const isTargetAdmin = u.role === "admin";
                                return (
                                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 hover:bg-gold-50 transition-colors duration-150">
                                        <td className="py-3 px-6">
                                            <div className="flex items-center gap-3">
                                                <div className={`h-8 w-8 rounded-full ${isTargetAdmin ? "bg-slate-900 text-white" : "bg-gold-100 text-gold-700"} flex items-center justify-center text-xs font-bold`}>
                                                    {(u.name || u.email).slice(0, 1).toUpperCase()}
                                                </div>
                                                <div className="text-sm font-medium text-slate-900">
                                                    {u.name}
                                                    {isSelf && <span className="ml-2 text-xs text-gold-600">(你)</span>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-6 text-sm text-slate-700 font-mono">{u.email}</td>
                                        <td className="py-3 px-6 text-center">
                                            {isTargetAdmin ? (
                                                <Badge className="bg-slate-900 text-white hover:bg-slate-900 border-0 gap-1">
                                                    <ShieldCheck className="h-3 w-3" /> 管理员
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-0">员工</Badge>
                                            )}
                                        </td>
                                        <td className="py-3 px-6 text-sm text-gold-500 font-mono">
                                            {u.last_login_at ? formatDate(u.last_login_at) : <span className="text-slate-400 italic">未登录</span>}
                                        </td>
                                        <td className="py-3 px-6 text-sm text-gold-500 font-mono">{formatDate(u.created_at)}</td>
                                        <td className="py-3 px-6 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    data-testid={`toggle-role-${u.id}`}
                                                    variant="ghost"
                                                    size="sm"
                                                    disabled={isSelf}
                                                    onClick={() => toggleRole(u)}
                                                    className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 gap-1.5 disabled:opacity-30"
                                                    title={isTargetAdmin ? "降级为员工" : "提升为管理员"}
                                                >
                                                    {isTargetAdmin ? <ShieldOff className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                                                    <span className="hidden lg:inline text-xs">{isTargetAdmin ? "改为员工" : "设为管理员"}</span>
                                                </Button>
                                                <Button
                                                    data-testid={`reset-password-${u.id}`}
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openReset(u)}
                                                    className="text-slate-600 hover:text-gold-700 hover:bg-gold-50 gap-1.5"
                                                    title="重置密码"
                                                >
                                                    <KeyRound className="h-3.5 w-3.5" />
                                                    <span className="hidden lg:inline text-xs">重置密码</span>
                                                </Button>
                                                <Button
                                                    data-testid={`delete-user-${u.id}`}
                                                    variant="ghost"
                                                    size="sm"
                                                    disabled={isSelf}
                                                    onClick={() => del(u)}
                                                    className="text-red-600 hover:bg-red-50 disabled:opacity-30"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                )}
            </Card>

            <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
                <DialogContent className="max-w-md" data-testid="reset-password-dialog">
                    <DialogHeader>
                        <DialogTitle>重置密码</DialogTitle>
                    </DialogHeader>
                    {resetTarget && (
                        <form onSubmit={submitReset} className="space-y-4 pt-2">
                            <div className="rounded-md bg-gold-50 border border-slate-200 p-3">
                                <div className="text-xs uppercase tracking-wider text-gold-500 font-semibold">目标账户</div>
                                <div className="text-sm font-medium text-slate-900 mt-1">{resetTarget.name}</div>
                                <div className="text-xs text-gold-500 font-mono">{resetTarget.email}</div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>新密码 * (至少 6 位)</Label>
                                <Input
                                    data-testid="reset-password-input"
                                    type="text"
                                    minLength={6}
                                    value={resetPassword}
                                    onChange={(e) => setResetPassword(e.target.value)}
                                    placeholder="请输入新密码"
                                    required
                                    className="font-mono"
                                    autoFocus
                                />
                                <div className="text-xs text-gold-500">重置后，请把新密码告知该员工。旧密码将立即失效。</div>
                            </div>
                            <DialogFooter className="pt-2">
                                <Button type="button" variant="outline" onClick={() => setResetTarget(null)}>取消</Button>
                                <Button type="submit" data-testid="submit-reset-password-btn" disabled={resetSaving} className="bg-gold-600 hover:bg-gold-700 text-white">
                                    {resetSaving ? "重置中..." : "确认重置"}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
